import React, { HtmlHTMLAttributes, useState } from 'react';
import classNames from 'classnames';
import { message } from 'antd';
import { ProductType } from '@meitu/subscribe-core';
import {
  getIncomeSource,
  getDisplayPrice,
  getPayChannels,
  isAppStorePay,
  isWeb,
} from '@/utils/vip';
import { openBrowser } from '@/utils/native';
import { getUrlParam } from '@/utils';
import WeChartIcon from '@/assets/image/vip/weChat.png';
import AlipayIcon from '@/assets/image/vip/alipay.png';
import { SubscribeType, isTeamSubscription, subscribeType } from '@/constants';
import { Checkbox, Tooltip } from 'antd';
import { BusinessType, PromotionType, TabType } from '@/constants/subscription';

import styles from './index.module.css';
import { useAtom } from 'jotai';
import {
  auditVersionStatusAtom,
  businessTypeAtom,
  curProductAtom,
  curTabAtom,
  hasFetchUserInfoAtom,
  loadingAtom,
  qrCodeUrlAtom,
  seatsAtom,
  userInfoAtom,
  vipInfoAtom,
  visibleBusinessAtom,
  visibleExchangeAtom,
  visibleInvoicingAtom,
} from '@/store';
import { track } from '@/utils/common';
import { handleJumpLogin } from '@/utils/user';

const channelsMap: any = {
  weixin: '微信',
  alipay: '支付宝',
};

const ProductPay = () => {
  const type = getUrlParam('type');
  const [checked, setChecked] = useState<boolean>(false);
  const [_, setVisibleExchange] = useAtom(visibleExchangeAtom);
  const [userInfo] = useAtom(userInfoAtom);
  const [curProduct] = useAtom(curProductAtom);
  const [loading] = useAtom(loadingAtom);
  const [curTab] = useAtom(curTabAtom);
  const [auditVersionStatus] = useAtom(auditVersionStatusAtom);
  const [seats] = useAtom(seatsAtom);
  const [vipInfo] = useAtom(vipInfoAtom);
  const [, setVisibleInvoicing] = useAtom(visibleInvoicingAtom);
  const [, setVisibleBusiness] = useAtom(visibleBusinessAtom);
  const [, setBusinessType] = useAtom(businessTypeAtom);
  const [hasFetchUserInfo] = useAtom(hasFetchUserInfoAtom);
  const [qrCodeUrl] = useAtom(qrCodeUrlAtom);

  const noLogin = hasFetchUserInfo && !userInfo?.id;

  const handleJumpAuth = () => {
    const url = 'https://pro.meitu.com/posterlabs/rules/web/powerOfAttorney.html';

    if (isWeb) {
      window.open(url);
    } else {
      openBrowser(url);
    }
  };

  // 页面第一次加载商品并请求交易二维码的时候才需要展示骨架屏
  const showSkeleton = !curProduct && !qrCodeUrl;

  const promotion = (curProduct?.promotions || [])[0] || {};
  // 是否免费试用商品, 2、4
  const isFreeTrial =
    promotion.promotionType === PromotionType.RecommendedFreeTrial ||
    promotion.promotionType === PromotionType.SalesFreeTrial;
  // 免费试用文案
  let freeTrialText = '';
  if (isFreeTrial) {
    let unit = '';
    switch (promotion?.promotionDuration?.period) {
      case 0:
        unit = '天';
        break;
      case 1:
        unit = '月';
        break;
      case 2:
        unit = '季';
        break;
      case 3:
        unit = '年';
        break;
      default:
    }
    freeTrialText = `${promotion.promotionDuration?.duration || ''}${unit}免费试用`;
  }

  // 支付渠道
  const payChannels: string[] = getPayChannels(curProduct) || [];

  const getTermInfo = () => {
    const info = {
      text: isWeb ? '美图设计室会员服务协议' : '美图秀秀粉钻SVIP会员协议',
      url: isWeb
        ? 'https://pro.meitu.com/posterlabs/rules/global/vipServiceAgreementPlusWeb.html'
        : 'https://pro.meitu.com/posterlabs/rules/global/vipServiceAgreement-web.html',
    };

    if (subscribeType === SubscribeType.LOGO_UNLIMITED) {
      info.text = '美图设计类服务协议';
      info.url = 'https://pro.meitu.com/xiuxiu/agreements/ai-logo.html';
    } else if (curTab === TabType.MEIDOU) {
      info.text = '美豆充值协议';
      info.url = 'https://www.designkit.cn/meidou-service';
    } else if (subscribeType === SubscribeType.CONSUMABLE && curTab === TabType.CUTOUT) {
      info.text = '抠图服务协议';
      info.url = `https://pro.meitu.com/posterlabs/rules/web/koutu-service.html`;
    } else if (type === 'svip') {
      // 批处理
      info.text = '美图秀秀批处理服务协议';
      info.url = 'https://pc.meitu.com/terms-svip';
    }
    return info;
  };

  const handleJumpTerm = () => {
    let params = {};

    if (isWeb) {
      params = {
        class: 'vip_tk',
        name_id: curProduct?.productId,
      };
    } else {
      params = {
        category: type === 'svip' ? '批处理条款' : '会员条款',
        product_id: curProduct?.productId,
      };
    }

    track({
      event_id: 'vip_page_click',
      event_name: '会员落地页点击',
      params,
    });

    let prefix = process.env.REACT_APP_ENV;

    if (process.env.REACT_APP_ENV === 'release') {
      prefix = '';
    }

    let url = getTermInfo().url;

    if (isWeb) {
      window.open(url);
    } else {
      openBrowser(url);
    }
  };

  const handleClickExchangeEntry = () => {
    let params = {};
    if (isWeb) {
      params = {
        class: '兑换码开通',
        name_id: curProduct?.productId,
        country_code: curProduct?.countryCode || '',
      };
    } else {
      params = {
        category: '兑换码开通',
        product_id: curProduct?.productId,
        country_code: curProduct?.countryCode || '',
      };
    }

    track({
      event_id: 'vip_page_click',
      event_name: '会员落地页点击',
      params,
    });

    if (!userInfo?.id) {
      handleJumpLogin();
      return;
    }
    setVisibleExchange(true);
  };

  // 我要开票
  const handleClickInvoicing = () => {
    let params = {};
    if (isWeb) {
      params = {
        class: '我要开票',
        name_id: curProduct?.productId,
        country_code: curProduct?.countryCode || '',
      };
    } else {
      params = {
        category: '我要开票',
        product_id: curProduct?.productId,
        country_code: curProduct?.countryCode || '',
      };
    }

    track({
      event_id: 'vip_page_click',
      event_name: '会员落地页点击',
      params,
    });

    // 新需求逻辑 展示二维码
    if (!userInfo?.id) {
      message.warning('请先登录');
      return;
    }
    setVisibleInvoicing(true);
  };

  const renderTipText = () => {
    const payChannelsText = payChannels
      ?.map((item: string) => {
        return channelsMap[item];
      })
      ?.join('/');

    return (
      <>
        {payChannels.map((item, index) => (
          <img
            className={styles.icon}
            key={index}
            src={item === 'weixin' ? WeChartIcon : AlipayIcon}
            alt=""
          />
        ))}
        <span>{`${payChannelsText}扫码${
          curProduct?.productType === ProductType.Consumable ? '购买' : '开通'
        }`}</span>
      </>
    );
  };

  const renderEntrance = () => {
    if (subscribeType === SubscribeType.LOGO_UNLIMITED) {
      return null;
    }

    return (
      <div className={styles.entrances}>
        {isTeamSubscription ? (
          <div
            onClick={() => {
              setVisibleBusiness(true);
              setBusinessType(BusinessType.LargeAmount);
            }}
          >
            联系商务
          </div>
        ) : null}
        {curTab === TabType.MEIDOU ? (
          <>
            {isTeamSubscription ? <div className={styles.divider}></div> : null}
            <div onClick={() => window.open('https://www.designkit.cn/meidou-introduce')}>
              常见问题
            </div>
            {!isTeamSubscription ? <div className={styles.divider}></div> : null}
          </>
        ) : null}
        {isTeamSubscription ? null : <div onClick={handleClickInvoicing}>我要开票</div>}
        {!isAppStorePay() && // Mac应用商店版不展示
        type !== 'svip' && // 批处理不展示
        !isTeamSubscription && // 团队版不展示
        curTab === TabType.SUB ? (
          // 美豆类型下的会员充值tab
          <>
            <div className={styles.divider}></div>
            <div onClick={handleClickExchangeEntry}>兑换码开通</div>
          </>
        ) : null}
        {curTab === TabType.SUB && isWeb ? (
          <>
            <div className={styles.divider}></div>
            <div
              onClick={() => {
                setVisibleBusiness(true);
                setBusinessType(BusinessType.Commercial);

                track({
                  event_id: 'vip_page_click',
                  event_name: '会员落地页点击',
                  params: {
                    class: 'consult_commercial',
                  },
                });
              }}
            >
              咨询企业商用
            </div>
          </>
        ) : null}
      </div>
    );
  };

  let payPrice = getDisplayPrice(curProduct).payPrice;

  // 团队版下的支付价计算
  if (isTeamSubscription && subscribeType === SubscribeType.SUBSCRIPTION) {
    // 取第一个优惠信息
    const promotionInfo: any = curProduct?.promotions && curProduct.promotions[0];
    // 是否是阶梯优惠商品类型
    const isTieredDiscounts = promotionInfo?.promotionType === PromotionType.TieredDiscounts;
    // 如果是阶梯优惠，支付价格（promotion_price.price * enjoy_num + (用户选择席位 - enjoy_num) * product_price.price）
    // 如果是非阶梯优惠，支付价格（payPrice * 用户选择席位）
    payPrice = isTieredDiscounts
      ? +(
          getDisplayPrice(curProduct).payPrice * promotionInfo.enjoyNum +
          (seats - promotionInfo.enjoyNum) * (+(curProduct?.productPrice?.price || 0) / 100)
        ).toFixed(2)
      : +(getDisplayPrice(curProduct).payPrice * seats).toFixed(2);
  }

  return (
    <div
      className={classNames(styles.module, {
        [styles.meidou]: SubscribeType.MEIDOU === subscribeType,
      })}
    >
      <div className={styles.top}>
        <div className={classNames(styles.payPrice, showSkeleton && styles.skeleton)}>
          {!isFreeTrial ? (
            <>
              支付：
              <span className={styles.symbol}>{getDisplayPrice(curProduct).moneySymbol}</span>
              <span className={styles.price}>{payPrice || 0}</span>
            </>
          ) : (
            <span className={styles.freeTrialText}>{freeTrialText}</span>
          )}
        </div>

        <div className={styles.qrcodeBox}>
          {!showSkeleton &&
          qrCodeUrl &&
          (!auditVersionStatus || (auditVersionStatus && checked)) ? (
            <img className={styles.qrcode} src={qrCodeUrl} alt="" />
          ) : (
            <div
              className={classNames(
                styles.qrcode,
                !(noLogin && curProduct?.productId) ? styles.skeleton : styles.noLoginQrcode
              )}
            >
              {noLogin && curProduct?.productId ? (
                <div className={styles.noLogin} onClick={handleJumpLogin}>
                  点击登录
                </div>
              ) : loading ? (
                <div className={styles.noLogin}>加载中</div>
              ) : !checked && auditVersionStatus ? (
                <div className={styles.noLogin}>请先同意协议</div>
              ) : null}
            </div>
          )}
        </div>

        <div className={classNames(styles.tip, showSkeleton && styles.skeleton)}>
          {renderTipText()}
        </div>
      </div>
      <div className={styles.bottom}>
        <div
          className={classNames(styles.term, showSkeleton && styles.skeleton)}
          style={
            auditVersionStatus
              ? {
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: 18,
                }
              : {}
          }
        >
          {auditVersionStatus ? (
            <Tooltip open={!checked} placement="bottom" title="请先阅读并同意协议">
              <Checkbox
                checked={checked}
                style={{ marginRight: 5 }}
                onChange={(e) => setChecked(e.target.checked)}
              ></Checkbox>
            </Tooltip>
          ) : null}
          <span>{auditVersionStatus ? '同意' : '支付即视为您已同意'}</span>
          <br />
          <span className={styles.link} onClick={handleJumpTerm}>
            《{getTermInfo().text}》
          </span>
          {isTeamSubscription && subscribeType === SubscribeType.SUBSCRIPTION ? (
            <span className={styles.link} onClick={handleJumpAuth}>
              《商用授权书》
            </span>
          ) : null}
        </div>
        {renderEntrance()}
      </div>
    </div>
  );
};
export default React.memo(ProductPay);
